import Dexie, { Table } from 'dexie';
import {
  Fish,
  FishLength,
  FishLimit,
  FishLimitMoreNote,
  FishType,
  FishesFishType,
  FishingTechnique,
  MoreNote,
  NoaaStation,
  State,
} from '@core/models/reference-data.models';
import { FishStat, FishStatImage } from '@core/models/fish-stat.model';

export interface MetaRow {
  id: number;
  data_version: number;
  schema_version: number;
  last_synced_at: string | null;
}

/**
 * IndexedDB-backed local cache.
 *
 * v1 schema: reference data + meta
 * v2 schema: adds fish_stats (the user's catches) + fish_stat_images.
 *            Catches are stored locally as the source of truth — the
 *            server is one of potentially many clients, so offline
 *            creates stay local and sync when connectivity returns.
 *
 * Schema changes must bump the version number AND preserve prior
 * version's stores() clause unchanged, per Dexie migration rules.
 * A stores() call with no arguments deletes a table.
 */
export class SaltwaterUpDb extends Dexie {
  states!: Table<State, number>;
  fish_types!: Table<FishType, number>;
  fishes!: Table<Fish, number>;
  fishes_fish_types!: Table<FishesFishType, number>;
  fishing_techniques!: Table<FishingTechnique, number>;
  fish_lengths!: Table<FishLength, number>;
  fish_limits!: Table<FishLimit, number>;
  fish_limits_more_notes!: Table<FishLimitMoreNote, number>;
  more_notes!: Table<MoreNote, number>;
  noaa_stations!: Table<NoaaStation, number>;
  meta!: Table<MetaRow, number>;

  /**
   * User's catches. Auto-incrementing local primary key (matches the
   * iOS app's `fish_stats.id` column, which sqlite auto-assigned).
   * remote_id is populated after a successful POST to the server.
   */
  fish_stats!: Table<FishStat, number>;

  /**
   * Photo blobs keyed by fish_stat_id. Stored in a separate table so
   * a FishStat list-fetch doesn't pull megabytes of image data per row.
   */
  fish_stat_images!: Table<FishStatImage, number>;

  constructor() {
    super('saltwaterup');

    this.version(1).stores({
      states:                 'id, sort_order',
      fish_types:             'id, sort_order',
      fishes:                 'id, sort_order',
      fishes_fish_types:      'id, fish_id, fish_type_id, [fish_id+fish_type_id]',
      fishing_techniques:     'id, sort_order',
      fish_lengths:           'id, length',
      fish_limits:            'id, fish_id, state_id, [fish_id+state_id]',
      fish_limits_more_notes: 'id, fish_limit_id, more_note_id',
      more_notes:             'id, sort_order',
      noaa_stations:          'id, is_active',
      meta:                   'id',
    });

    this.version(2).stores({
      // Indexes chosen to support the My Catches list queries:
      //   - date_and_time (sorting, month/year filtering)
      //   - remote_id (lookup when a sync response comes back)
      //   - is_update_needed (find catches that still need to sync)
      //   - user_id (future: multi-account support)
      fish_stats:
        '++id, date_and_time, remote_id, is_update_needed, user_id',
      fish_stat_images: 'fish_stat_id',
    });
  }
}

export const db = new SaltwaterUpDb();
